/**
 * Recurrence matching for incomes, expenses and loans.
 *
 * This logic previously existed four times (calculateBalanceOnDate,
 * generateBalanceProjections, and twice inside Calendar's hand-rolled past
 * replay), with the copies drifting apart. It lives here once.
 *
 * One deliberate correction: weekly items previously matched on `getUTCDay()`
 * while monthly items matched on local `getDate()`. For a Date at local
 * midnight in a western timezone those agree, so this is a no-op for US users,
 * but mixing the two was a latent bug. Everything is local now.
 */

import { Expense, Income, Loan } from '../../types';
import { daysInMonth, startOfDay, toLocalDay } from './dates';

/** Inclusive start / end gating shared by incomes and expenses. */
function withinActiveWindow(
  date: Date,
  startDate?: Date | string | null,
  endDate?: Date | string | null
): boolean {
  if (startDate) {
    if (date < startOfDay(toLocalDay(startDate))) return false;
  }
  if (endDate) {
    if (date > startOfDay(toLocalDay(endDate))) return false;
  }
  return true;
}

/** True when a monthly item due on `dueDay` falls on `date`, clamping so an
 *  item due on the 31st still lands in February. */
function matchesMonthlyDay(date: Date, dueDay: number): boolean {
  const clamped = Math.min(
    Math.max(1, dueDay),
    daysInMonth(date.getFullYear(), date.getMonth())
  );
  return date.getDate() === clamped;
}

export function incomeOccursOn(income: Income, date: Date): boolean {
  if (!income.isActive) return false;
  if (!withinActiveWindow(date, income.startDate, income.endDate)) return false;

  switch (income.frequency) {
    case 'monthly':
      return income.payDay !== undefined && matchesMonthlyDay(date, income.payDay);

    case 'biweekly': {
      // biweeklyStartDate anchors the fortnightly cadence — it is what the
      // 14-day count is measured from, and separately the earliest possible
      // payday. `startDate` above is the window, and the two are not the same
      // thing: a schedule can be anchored in the past and begin later.
      if (!income.biweeklyStartDate) return false;
      const start = startOfDay(toLocalDay(income.biweeklyStartDate));
      if (date < start) return false;
      // Compare via UTC of the local Y/M/D so a DST shift can't make a
      // 14-day gap measure 13.96 days and skip a payday.
      const a = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
      const b = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
      const diffDays = Math.round((b - a) / 86400000);
      return diffDays >= 0 && diffDays % 14 === 0;
    }

    case 'weekly':
      return income.payDay !== undefined && date.getDay() === income.payDay % 7;

    case 'one-time': {
      if (!income.depositDate) return false;
      const d = startOfDay(toLocalDay(income.depositDate));
      return d.getTime() === date.getTime();
    }

    default:
      return false;
  }
}

export function expenseOccursOn(expense: Expense, date: Date): boolean {
  if (!expense.isActive) return false;
  if (!withinActiveWindow(date, expense.startDate, expense.endDate)) return false;

  switch (expense.frequency) {
    case 'monthly':
      return expense.dueDay !== undefined && matchesMonthlyDay(date, expense.dueDay);

    case 'weekly':
      return expense.dueDay !== undefined && date.getDay() === expense.dueDay % 7;

    case 'one-time': {
      if (!expense.dueDate) return false;
      const d = startOfDay(toLocalDay(expense.dueDate));
      return d.getTime() === date.getTime();
    }

    default:
      return false;
  }
}

export function loanPaymentOccursOn(loan: Loan, date: Date): boolean {
  if (!loan.isActive) return false;
  if (!loan.dueDay) return false;
  if (!withinActiveWindow(date, loan.startDate, loan.maturityDate)) {
    return false;
  }
  return matchesMonthlyDay(date, loan.dueDay);
}
